import React, { useMemo } from 'react';
import { RichTextBlock } from 'prismic-reactjs';
import Section from '@components/section';
import Wrapper from '@components/wrapper.styled';
import generateId from '@helpers/generate-id';
import Text from '@components/text';
import { ImageProps } from '@components/image';
import AnimatedElement from '@components/animated-element';
import {
  StyledGrid,
  StyledBlock,
  StyledTextContent,
  Container,
  FeatureDescription,
  PinImage,
  HeadingWrapper,
  PinImageWrapper,
} from './feature-map.styled';

interface FeatureMapItemType {
  title?: {
    text: string;
  };
  description?: {
    richText: RichTextBlock[];
  };
}

export interface FeatureMapProps {
  title?: {
    text?: string;
  };
  image?: ImageProps;
  items?: FeatureMapItemType[];
}

const FeatureMap: React.FC<FeatureMapProps> = ({ title, image, items = [] }) => {
  const idGenerator = useMemo(() => generateId(), []);

  const hasItems = useMemo(() => {
    if (!items.length) return false;
    const firstItem = items[0];
    return firstItem?.title?.text !== '' || (firstItem?.description?.richText?.length ?? 0) > 0;
  }, [items]);

  const renderGrid = useMemo(() => {
    if (!items.length) return null;

    return (
      <StyledGrid role="list" aria-label="Feature list">
        {items.map((item) => (
          <StyledBlock role="listitem" key={idGenerator.next().value}>
            <HeadingWrapper>
              {item.title?.text && (
                <Text
                  type="title"
                  tag="h3"
                  stringText={item.title.text}
                  fontSize="heading4"
                  fontColor="black"
                  margin="none"
                />
              )}
              <PinImageWrapper>
                <AnimatedElement
                  animationType="slideInLeft"
                  delay={0}
                  threshold={0.1}
                  rootMargin="50px"
                >
                  <PinImage {...image} objectFit="contain" />
                </AnimatedElement>
              </PinImageWrapper>
            </HeadingWrapper>
            {item.description?.richText && item.description.richText.length > 0 && (
              <FeatureDescription
                type="description"
                richText={item.description?.richText}
                fontSize="body"
                fontColor="muted"
                margin="none"
              />
            )}
          </StyledBlock>
        ))}
      </StyledGrid>
    );
  }, [items, idGenerator, image]);

  return (
    <Section backgroundColor="white">
      <Wrapper>
        <Container>
          <StyledTextContent>
            {title?.text && (
              <Text
                type="title"
                tag="h2"
                stringText={title?.text}
                fontSize="heading3"
                fontColor="secondary"
                margin="none"
              />
            )}
          </StyledTextContent>
          {hasItems && renderGrid}
        </Container>
      </Wrapper>
    </Section>
  );
};

export default React.memo(FeatureMap);
